function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = '__';

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
    if (document.querySelector('.product-form__buttons button')) {
      this.addToCartBtn = document.querySelector('.product-form__buttons button');
      this.totalContent = this.addToCartBtn.querySelector('.add_to_cart_total');
    }
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();

  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
        let price = 0;
        if (this.addToCartBtn) {
          const dataPrice = this.addToCartBtn.getAttribute('data-price');

          if (dataPrice !== null) {
            price = dataPrice.replace(',', '.');
          }
        }
        let total = this.input.value * parseFloat(price);

        if (this.totalContent) {
          this.totalContent.textContent = `${shopCurrency.symbol}${String(total.toFixed(2)).replace('.', ',')}`;
        }

      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
    if (value > 0) {
      let price = 0;
      if (this.addToCartBtn) {
          const dataPrice = this.addToCartBtn.getAttribute('data-price');

          if (dataPrice !== null) {
            price = dataPrice.replace(',', '.');
          }
        }
      let total = this.input.value * parseFloat(price);

      if (this.totalContent) {
        this.totalContent.textContent = `${shopCurrency.symbol}${String(total.toFixed(2)).replace('.', ',')}`;
      }

    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    // FIX: Read layout property (getBoundingClientRect) BEFORE any DOM writes
    // to avoid a forced reflow. Writing a CSS custom property after a layout read
    // is fine; writing it after a style mutation would force re-layout.
    const headerBottom = parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${headerBottom}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    // FIX: Wrap the layout read (getBoundingClientRect) and subsequent write
    // (setProperty) inside requestAnimationFrame so they execute at the start of
    // a fresh frame, after the browser has already committed the resized layout.
    // This prevents the resize handler from forcing a synchronous reflow.
    requestAnimationFrame(() => {
      if (this.header) {
        const headerBottom = parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset);
        document.documentElement.style.setProperty('--header-bottom-position', `${headerBottom}px`);
      }
      document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    });
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    // FIX: Batch all layout reads together before any writes to avoid forced reflow.
    // Reading offsetLeft/clientWidth on multiple elements in sequence is safe as long
    // as no DOM writes are interleaved between them.
    const offsetLeft0 = this.sliderItemsToShow[0].offsetLeft;
    const offsetLeft1 = this.sliderItemsToShow[1].offsetLeft;
    const sliderClientWidth = this.slider.clientWidth;
    this.sliderItemOffset = offsetLeft1 - offsetLeft0;
    this.slidesPerPage = Math.floor(
      (sliderClientWidth - offsetLeft0) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    // FIX: Read all layout properties once at the top of update() so that
    // isSlideVisible() can reuse cached values and never triggers a mid-write reflow.
    const scrollLeft = this.slider.scrollLeft;
    const clientWidth = this.slider.clientWidth;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0], 0, scrollLeft, clientWidth) && scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1], 0, scrollLeft, clientWidth)) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  // FIX: Accept pre-read scrollLeft and clientWidth so this method never reads
  // layout properties itself (which would force a reflow if called after a write).
  isSlideVisible(element, offset = 0, scrollLeft = this.slider.scrollLeft, clientWidth = this.slider.clientWidth) {
    const lastVisibleSlide = clientWidth + scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

// Custom slider

for(const slideItem of document.querySelectorAll('.banner__slider-item')) {
  slideItem.addEventListener('click', function () {
    let activeIndex = document.querySelector('.banner__slider-active'),
        activeElement = document.querySelector(`.box-${activeIndex.getAttribute('data-index')}`),
        currentElement = document.querySelector(`.box-${this.getAttribute('data-index')}`);

    activeElement.removeAttribute('tabindex');
    activeElement.setAttribute('aria-hidden', 'true');
    activeElement.classList.remove('show-box');

    document.querySelectorAll('.banner__slider-active').forEach(element => {
      element.classList.remove('banner__slider-active');
    });

    currentElement.setAttribute('tabindex', '-1');
    currentElement.setAttribute('aria-hidden', 'false');
    currentElement.classList.add('show-box');
    this.classList.add('banner__slider-active');

    document.querySelectorAll(`.banner__slider-item[data-index="${this.getAttribute('data-index')}"]`).forEach(element => {
      element.classList.add('banner__slider-active');
    });


  });
}

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }

  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.pageNumber) {
      return `${window.location.pathname}?page=${window.pageNumber}`;
    } else {
      return `${window.location.pathname}`;
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

/**
 * Curated Collections Slider - OPTIMIZED VERSION
 * Performance improvements:
 * - Cached measurements with TTL
 * - Throttled scroll handlers
 * - Batched DOM reads/writes
 * - ResizeObserver instead of resize events
 */

(function() {
  'use strict';

  function throttle(fn, delay) {
    let lastCall = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastCall < delay) return;
      lastCall = now;
      return fn.apply(this, args);
    };
  }

  function initSliders() {
    const sliders = document.querySelectorAll('[data-slider]');
    
    sliders.forEach(slider => {
      const prevBtn = slider.parentElement.querySelector('[data-slider-prev]');
      const nextBtn = slider.parentElement.querySelector('[data-slider-next]');
      
      if (!prevBtn || !nextBtn) return;

      const autoplayEnabled = slider.dataset.autoplay === 'true';
      const autoplayDelay = parseInt(slider.dataset.autoplayDelay) || 3000;
      
      let autoplayInterval = null;

      let isDragging = false;
      let startPos = 0;
      let prevTranslate = 0;

      const measurementCache = {
        slideWidth: null,
        timestamp: 0,
        TTL: 1000,
        
        get(measureFn) {
          const now = Date.now();
          if (this.slideWidth !== null && (now - this.timestamp) < this.TTL) {
            return this.slideWidth;
          }
          this.slideWidth = measureFn();
          this.timestamp = now;
          return this.slideWidth;
        },
        
        invalidate() {
          this.slideWidth = null;
          this.timestamp = 0;
        }
      };

      function getSlideWidth() {
        return measurementCache.get(() => {
          const slide = slider.querySelector('.curated-collections__slide');
          if (!slide) return 0;
          
          const slideWidth = slide.offsetWidth;
          const computedStyle = window.getComputedStyle(slider);
          const gap = parseInt(computedStyle.gap) || 20;
          
          return slideWidth + gap;
        });
      }

      function getScrollAmount() {
        const viewportWidth = window.innerWidth;
        const slideWidth = getSlideWidth();
        
        return viewportWidth >= 1024 ? slideWidth * 2 : slideWidth;
      }

      function updateButtons() {
        const scrollLeft = slider.scrollLeft;
        const scrollWidth = slider.scrollWidth;
        const clientWidth = slider.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        
        requestAnimationFrame(() => {
          prevBtn.disabled = scrollLeft <= 0;
          nextBtn.disabled = scrollLeft >= maxScroll - 1;
        });
      }

      const throttledUpdateButtons = throttle(updateButtons, 100);

      function startAutoplay() {
        if (!autoplayEnabled) return;
        
        stopAutoplay();
        
        autoplayInterval = setInterval(() => {
          const scrollLeft = slider.scrollLeft;
          const scrollWidth = slider.scrollWidth;
          const clientWidth = slider.clientWidth;
          const maxScroll = scrollWidth - clientWidth;
          
          requestAnimationFrame(() => {
            if (scrollLeft >= maxScroll - 1) {
              slider.scrollTo({
                left: 0,
                behavior: 'smooth'
              });
            } else {
              const scrollAmount = getScrollAmount();
              slider.scrollBy({
                left: scrollAmount,
                behavior: 'smooth'
              });
            }
          });
        }, autoplayDelay);
      }

      function stopAutoplay() {
        if (autoplayInterval) {
          clearInterval(autoplayInterval);
          autoplayInterval = null;
        }
      }

      prevBtn.addEventListener('click', () => {
        const scrollAmount = getScrollAmount();
        slider.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth'
        });
        
        if (autoplayEnabled) {
          stopAutoplay();
          startAutoplay();
        }
      });

      nextBtn.addEventListener('click', () => {
        const scrollAmount = getScrollAmount();
        slider.scrollBy({
          left: scrollAmount,
          behavior: 'smooth'
        });
        
        if (autoplayEnabled) {
          stopAutoplay();
          startAutoplay();
        }
      });

      slider.addEventListener('scroll', throttledUpdateButtons);

      const resizeObserver = new ResizeObserver(() => {
        measurementCache.invalidate();
        updateButtons();
      });
      resizeObserver.observe(slider);

      updateButtons();
      
      const images = slider.querySelectorAll('img');
      images.forEach(img => {
        img.addEventListener('dragstart', (e) => e.preventDefault());
      });

      slider.addEventListener('touchstart', touchStart, { passive: true });
      slider.addEventListener('mousedown', touchStart);
      slider.addEventListener('touchmove', touchMove, { passive: false });
      slider.addEventListener('mousemove', touchMove);
      slider.addEventListener('touchend', touchEnd);
      slider.addEventListener('mouseup', touchEnd);
      slider.addEventListener('mouseleave', touchEnd);

      function touchStart(event) {
        isDragging = true;
        startPos = getPositionX(event);
        prevTranslate = slider.scrollLeft;
        slider.style.cursor = 'grabbing';
        slider.style.scrollBehavior = 'auto';
        
        stopAutoplay();
      }

      function touchMove(event) {
        if (!isDragging) return;
        
        const currentPosition = getPositionX(event);
        const diff = startPos - currentPosition;
        
        slider.scrollLeft = prevTranslate + diff;
        
        if (event.type === 'mousemove') {
          event.preventDefault();
        }
      }

      function touchEnd() {
        if (!isDragging) return;
        
        isDragging = false;
        slider.style.cursor = 'grab';
        slider.style.scrollBehavior = 'smooth';
        
        updateButtons();
        
        if (autoplayEnabled) {
          startAutoplay();
        }
      }

      function getPositionX(event) {
        return event.type.includes('mouse') 
          ? event.pageX 
          : event.touches[0].clientX;
      }

      slider.style.cursor = 'grab';

      let clickStartX = 0;
      let clickStartY = 0;
      
      slider.addEventListener('mousedown', (e) => {
        clickStartX = e.clientX;
        clickStartY = e.clientY;
      });

      slider.addEventListener('click', (e) => {
        const diffX = Math.abs(e.clientX - clickStartX);
        const diffY = Math.abs(e.clientY - clickStartY);
        
        if (diffX > 5 || diffY > 5) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);

      slider.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') {
          prevBtn.click();
          e.preventDefault();
        } else if (e.key === 'ArrowRight') {
          nextBtn.click();
          e.preventDefault();
        }
      });

      slider.setAttribute('tabindex', '0');

      slider.addEventListener('mouseenter', () => {
        if (autoplayEnabled) {
          stopAutoplay();
        }
      });

      slider.addEventListener('mouseleave', () => {
        if (autoplayEnabled && !isDragging) {
          startAutoplay();
        }
      });

      if (autoplayEnabled) {
        startAutoplay();
      }

      const section = slider.closest('[id^="curated-collections-"]');
      if (section) {
        const mutationObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.removedNodes.forEach((node) => {
              if (node === section || node.contains(section)) {
                stopAutoplay();
                resizeObserver.disconnect();
                mutationObserver.disconnect();
              }
            });
          });
        });
        
        mutationObserver.observe(document.body, { childList: true, subtree: true });
      }
    });
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSliders);
  } else {
    initSliders();
  }

  document.addEventListener('shopify:section:load', initSliders);
})();

// Tabs section
class TabsSection {
  constructor(container) {
    this.container = container;
    this.tabButtons = container.querySelectorAll('.tab-button');
    this.tabContents = container.querySelectorAll('.tab-content');
    this.sliders = container.querySelectorAll('.products-slider');
    
    this.init();
  }

  init() {
    this.setupTabs();
    this.setupSliders();
  }

  setupTabs() {
    this.tabButtons.forEach((button, index) => {
      button.addEventListener('click', () => {
        // OPTIMIZATION: Batch DOM writes in next frame
        requestAnimationFrame(() => {
          this.tabButtons.forEach(btn => btn.classList.remove('active'));
          this.tabContents.forEach(content => content.classList.remove('active'));
          
          button.classList.add('active');
          this.tabContents[index].classList.add('active');
        });
      });
    });
  }

  setupSliders() {
    this.sliders.forEach(slider => {
      const track = slider.querySelector('.products-track');
      const prevBtn = slider.parentNode.querySelector('.slider-button.prev');
      const nextBtn = slider.parentNode.querySelector('.slider-button.next');
      const cards = track.querySelectorAll('.product-card');
      const progressBar = slider.parentElement.querySelector('.slider-progress-bar');
      
      if (cards.length === 0) return;

      let currentIndex = 0;
      let startX = 0;
      let currentX = 0;
      let isDragging = false;
      let startTransform = 0;
      let autoSlideInterval = null;
      let pendingUpdate = null;

      const AUTO_SLIDE_DELAY = 3000;

      const measurementCache = {
        cardWidth: null,
        visibleCards: null,
        maxIndex: null,
        trackWidth: null,
        sliderWidth: null,
        timestamp: 0,
        TTL: 500, // 500ms cache
        
        invalidate() {
          this.cardWidth = null;
          this.visibleCards = null;
          this.maxIndex = null;
          this.trackWidth = null;
          this.sliderWidth = null;
          this.timestamp = 0;
        },
        
        isValid() {
          return this.cardWidth !== null && (Date.now() - this.timestamp) < this.TTL;
        }
      };

      // OPTIMIZATION: Cache card width to avoid repeated offsetWidth reads.
      // FIX: The cache-miss path reads offsetWidth synchronously. This is safe only
      // when called from the READ PHASE at the top of updateSlider(), before any
      // writes. Do NOT call getCardWidth() after a style/class write.
      const getCardWidth = () => {
        if (measurementCache.isValid() && measurementCache.cardWidth !== null) {
          return measurementCache.cardWidth;
        }
        
        // Read once and cache — must be called in a read-only phase
        const width = cards[0].offsetWidth + 20;
        measurementCache.cardWidth = width;
        measurementCache.timestamp = Date.now();
        return width;
      };

      // OPTIMIZATION: Cache visible cards calculation
      const getVisibleCards = () => {
        if (measurementCache.isValid() && measurementCache.visibleCards !== null) {
          return measurementCache.visibleCards;
        }
        
        const viewportWidth = window.innerWidth;
        let visible;
        
        if (viewportWidth <= 480) {
          visible = 1;
        } else if (viewportWidth <= 1024) {
          visible = 3;
        } else {
          visible = 3;
        }
        
        measurementCache.visibleCards = visible;
        measurementCache.timestamp = Date.now();
        return visible;
      };

      // OPTIMIZATION: Cache max index calculation
      const getMaxIndex = () => {
        if (measurementCache.isValid() && measurementCache.maxIndex !== null) {
          return measurementCache.maxIndex;
        }
        
        const visibleCards = getVisibleCards();
        const maxPossibleIndex = Math.max(0, cards.length - visibleCards);
        const maxIndex = Math.floor(maxPossibleIndex);
        
        measurementCache.maxIndex = maxIndex;
        measurementCache.timestamp = Date.now();
        return maxIndex;
      };

      // OPTIMIZATION: Batch all DOM reads together.
      // FIX: track.scrollWidth and slider.offsetWidth are layout properties.
      // Cache them alongside cardWidth so they are always read in the same
      // dedicated read phase inside updateSlider(), never after a style write.
      const getTranslateX = () => {
        const cardWidth = getCardWidth();
        const maxIndex = getMaxIndex();
        const visibleCards = getVisibleCards();
        const safeIndex = Math.min(currentIndex, maxIndex);
        
        if (window.innerWidth <= 1024 && visibleCards === 3) {
          // FIX: Use cached measurements when available instead of reading
          // scrollWidth/offsetWidth here, which could be after a style write.
          let trackWidth = measurementCache.trackWidth;
          let sliderWidth = measurementCache.sliderWidth;
          if (trackWidth == null || sliderWidth == null) {
            // Safe to read here only because getTranslateX is called from the
            // READ PHASE at the top of updateSlider(), before any writes.
            trackWidth = track.scrollWidth;
            sliderWidth = slider.offsetWidth;
            measurementCache.trackWidth = trackWidth;
            measurementCache.sliderWidth = sliderWidth;
          }
          const maxTranslate = trackWidth - sliderWidth;
          const calculatedTranslate = safeIndex * cardWidth;
          
          return Math.min(calculatedTranslate, maxTranslate);
        }
        
        return safeIndex * cardWidth;
      };

      // OPTIMIZATION: Batch DOM reads and writes separately
      const updateSlider = (smooth = true) => {
        // Cancel any pending updates
        if (pendingUpdate) {
          cancelAnimationFrame(pendingUpdate);
        }
        
        // READ PHASE: Get all measurements first
        const maxIndex = getMaxIndex();
        currentIndex = Math.max(0, Math.min(currentIndex, maxIndex));
        const translateX = getTranslateX();
        const progress = maxIndex > 0 ? (currentIndex / maxIndex) * 100 : 100;
        
        // WRITE PHASE: Batch all DOM writes in next frame
        pendingUpdate = requestAnimationFrame(() => {
          if (smooth) {
            track.style.transition = 'transform 0.4s ease';
          } else {
            track.style.transition = 'none';
          }
          
          track.style.transform = `translateX(-${translateX}px)`;
          prevBtn.disabled = currentIndex === 0;
          nextBtn.disabled = currentIndex >= maxIndex;
          
          if (progressBar) {
            progressBar.style.width = `${Math.min(progress, 100)}%`;
          }
          
          pendingUpdate = null;
        });
      };

      const startAutoSlide = () => {
        stopAutoSlide(); 
        autoSlideInterval = setInterval(() => {
          const maxIndex = getMaxIndex();
          
          if (currentIndex >= maxIndex) {
            currentIndex = 0;
          } else {
            currentIndex++;
          }
          
          updateSlider(true);
        }, AUTO_SLIDE_DELAY);
      };

      const stopAutoSlide = () => {
        if (autoSlideInterval) {
          clearInterval(autoSlideInterval);
          autoSlideInterval = null;
        }
      };

      const resetAutoSlide = () => {
        stopAutoSlide();
        startAutoSlide();
      };

      // Touch/Mouse start
      const handleStart = (e) => {
        isDragging = true;
        startX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
        currentX = startX;
        startTransform = getTranslateX();
        
        // OPTIMIZATION: Batch style changes
        requestAnimationFrame(() => {
          track.style.transition = 'none';
          slider.style.cursor = 'grabbing';
        });
        
        stopAutoSlide();
      };

      // Touch/Mouse move
      const handleMove = (e) => {
        if (!isDragging) return;
        
        e.preventDefault();
        currentX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
        const diff = currentX - startX;
        const newTransform = startTransform - diff;
        
        // Direct write during drag (no batching needed for smooth dragging)
        track.style.transform = `translateX(-${newTransform}px)`;
      };

      // Touch/Mouse end
      const handleEnd = () => {
        if (!isDragging) return;
        
        isDragging = false;
        
        // OPTIMIZATION: Batch reads first
        const diff = startX - currentX;
        const cardWidth = getCardWidth();
        const threshold = cardWidth / 3;
        const maxIndex = getMaxIndex();
        
        // Then update state
        if (Math.abs(diff) > threshold) {
          if (diff > 0 && currentIndex < maxIndex) {
            currentIndex++;
          } else if (diff < 0 && currentIndex > 0) {
            currentIndex--;
          }
        }
        
        // OPTIMIZATION: Batch write
        requestAnimationFrame(() => {
          slider.style.cursor = 'grab';
        });
        
        updateSlider(true);
        resetAutoSlide();
      };

      // Mouse events
      track.addEventListener('mousedown', handleStart);
      document.addEventListener('mousemove', handleMove);
      document.addEventListener('mouseup', handleEnd);
      
      // Touch events
      track.addEventListener('touchstart', handleStart, { passive: false });
      track.addEventListener('touchmove', handleMove, { passive: false });
      track.addEventListener('touchend', handleEnd);

      // Prevent click during drag
      track.addEventListener('click', (e) => {
        if (Math.abs(startX - currentX) > 5) {
          e.preventDefault();
        }
      }, true);

      // Button clicks
      prevBtn.addEventListener('click', () => {
        if (currentIndex > 0) {
          currentIndex--;
          updateSlider();
          resetAutoSlide();
        }
      });

      nextBtn.addEventListener('click', () => {
        const maxIndex = getMaxIndex();
        if (currentIndex < maxIndex) {
          currentIndex++;
          updateSlider();
          resetAutoSlide();
        }
      });

      slider.addEventListener('mouseenter', stopAutoSlide);
      slider.addEventListener('mouseleave', startAutoSlide);

      updateSlider();
      startAutoSlide();

      const resizeObserver = new ResizeObserver(() => {
        measurementCache.invalidate();
        currentIndex = 0;
        updateSlider();
        resetAutoSlide();
      });
      resizeObserver.observe(slider);
      
      const cleanUp = () => {
        stopAutoSlide();
        resizeObserver.disconnect();
        if (pendingUpdate) {
          cancelAnimationFrame(pendingUpdate);
        }
      };
      
      slider._cleanUp = cleanUp;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const tabsSections = document.querySelectorAll('.tabs-section');
  tabsSections.forEach(section => new TabsSection(section));
  if (document.querySelector('.price-item')) {
    const priceTotal = document.querySelector('.price-item').getAttribute('data-price');
    if(document.querySelector('.product-form__buttons button')) {
      const addToCartBtn = document.querySelector('.product-form__buttons button');
      addToCartBtn.setAttribute('data-price', priceTotal);
      if(addToCartBtn.querySelector('.add_to_cart_total')) {
        addToCartBtn.querySelector('.add_to_cart_total').textContent = `${shopCurrency.symbol}${priceTotal}`;
      }
    }
  }
});

document.addEventListener('shopify:section:load', () => {
  const tabsSections = document.querySelectorAll('.tabs-section');
  tabsSections.forEach(section => new TabsSection(section));
});